import { defineStore } from 'pinia'
import { ref, computed, readonly } from 'vue'
import { categoriesAPI } from '../api/modules/categories.js'

export const useCategoriesStore = defineStore('categories', () => {
  // State
  const categories = ref([])
  const categoryTree = ref([])
  const currentCategory = ref(null)
  const isLoading = ref(false)
  const error = ref(null)

  // Computed
  const hasCategories = computed(() => categories.value.length > 0)
  const rootCategories = computed(() => 
    categories.value.filter(category => !category.parent_id)
  )
  const getCategoryChildren = (parentId) => {
    return categories.value.filter(category => category.parent_id === parentId)
  }

  // Actions
  const setCategories = (categoriesData) => {
    categories.value = categoriesData
  }

  const setCategoryTree = (treeData) => {
    categoryTree.value = treeData
  }

  const setCurrentCategory = (category) => {
    currentCategory.value = category
  }

  const clearCategories = () => {
    categories.value = []
    categoryTree.value = []
    currentCategory.value = null
    error.value = null
  }

  const getCategories = async (params = {}) => {
    try {
      isLoading.value = true
      error.value = null
      
      const response = await categoriesAPI.getCategories(params)
      
      setCategories(response.data)
      
      return { success: true, data: response.data, meta: response.meta }
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to load categories'
      return { success: false, error: error.value }
    } finally {
      isLoading.value = false
    }
  }

  const getCategoryTree = async () => {
    try {
      isLoading.value = true
      error.value = null
      
      const response = await categoriesAPI.getCategoryTree()
      
      setCategoryTree(response.data)
      
      return { success: true, data: response.data }
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to load category tree'
      return { success: false, error: error.value }
    } finally {
      isLoading.value = false
    }
  }

  const getCategory = async (slug, params = {}) => {
    try {
      isLoading.value = true
      error.value = null
      
      const response = await categoriesAPI.getCategory(slug, params)
      
      setCurrentCategory(response.data)
      
      return { success: true, data: response.data }
    } catch (err) {
      error.value = err.response?.data?.message || 'Failed to load category'
      return { success: false, error: error.value }
    } finally {
      isLoading.value = false
    }
  }

  // Helper functions
  const findCategoryById = (categoryId) => {
    return categories.value.find(category => category.id === categoryId)
  }

  const findCategoryBySlug = (slug) => {
    return categories.value.find(category => category.slug === slug)
  }

  const updateCategory = (categoryId, updatedData) => {
    const index = categories.value.findIndex(category => category.id === categoryId)
    if (index !== -1) {
      categories.value[index] = { ...categories.value[index], ...updatedData }
    }
  }

  const removeCategory = (categoryId) => {
    const index = categories.value.findIndex(category => category.id === categoryId)
    if (index !== -1) {
      categories.value.splice(index, 1)
    }
  }

  const addCategory = (category) => {
    categories.value.push(category)
  }

  const buildCategoryPath = (categoryId) => {
    const path = []
    let currentCat = findCategoryById(categoryId)
    
    while (currentCat) {
      path.unshift(currentCat)
      currentCat = currentCat.parent_id ? findCategoryById(currentCat.parent_id) : null
    }
    
    return path
  }

  const getCategoryBreadcrumbs = (categoryId) => {
    return buildCategoryPath(categoryId).map(cat => ({
      id: cat.id,
      name: cat.name,
      slug: cat.slug
    }))
  }

  return {
    // State
    categories: readonly(categories),
    categoryTree: readonly(categoryTree),
    currentCategory: readonly(currentCategory),
    isLoading: readonly(isLoading),
    error: readonly(error),
    
    // Computed
    hasCategories,
    rootCategories,
    getCategoryChildren,
    
    // Actions
    setCategories,
    setCategoryTree,
    setCurrentCategory,
    clearCategories,
    getCategories,
    getCategoryTree,
    getCategory,
    
    // Helpers
    findCategoryById,
    findCategoryBySlug,
    updateCategory,
    removeCategory,
    addCategory,
    buildCategoryPath,
    getCategoryBreadcrumbs
  }
})
